import { useEffect } from 'react';
import { useSelector, shallowEqual } from 'react-redux';

import NsContent from 'components/common/ns-content/NsContent';
import { NsList } from 'components/common/ns-list/NsList';
import { NsSelector } from 'components/common/ns-selector/NsSelector';
import { useHasSmartMeter } from 'components/direct-sales/quote/hooks/useHasSmartMeter';
import { FUEL_TYPES, FUEL_NAMES, METER_READ_TYPES } from 'config/app-config';
import { useCurrentProduct } from 'components/direct-sales/quote/hooks/useCurrentProduct';
import {
  setMeterReadPreferences,
  selectMeterReadPreferences
} from 'state/user-preferences/user-preferences-slice';
import { pushEvent } from 'state/analytics/analytics-slice';
import { useAppDispatch } from 'hooks/useAppDispatch';
import NsFormGroup from '@britishgasenergy/react-nucleus/dist/nuc-components/NsFormGroup';
import { NsAnchor } from 'components/common/ns-anchor/NsAnchor';
import { actionUrl } from 'config/static-content';

export const testId = 'meterReadPreferencesTestId';

export const MeterReadPreferences = () => {
  const dispatch = useAppDispatch();
  const { isTOU } = useCurrentProduct();
  const selectedMeterReadPreferences = useSelector(
    selectMeterReadPreferences,
    shallowEqual
  );
  const { hasElectricitySmartMeter, hasGasSmartMeter } = useHasSmartMeter();
  const smartMeterByFuel = [
    ...(hasElectricitySmartMeter ? [FUEL_TYPES.ELECTRIC] : []),
    ...(hasGasSmartMeter ? [FUEL_TYPES.GAS] : [])
  ];
  const hasNoSmartMeter = !hasElectricitySmartMeter && !hasGasSmartMeter;
  const shouldNotRender = hasNoSmartMeter || isTOU;

  useEffect(() => {
    if (isTOU) {
      smartMeterByFuel.forEach((fuel) => {
        dispatch(
          setMeterReadPreferences({
            fuel,
            value: METER_READ_TYPES.HALF_HOURLY
          })
        );
        dispatch(
          pushEvent({
            category: 'portal_smartmeter_frequency_final',
            action: fuel,
            label: METER_READ_TYPES.HALF_HOURLY
          })
        );
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fuel =
    hasElectricitySmartMeter && hasGasSmartMeter ? 'dual' : smartMeterByFuel[0];

  let decoration;
  let selectedPreference;

  switch (fuel) {
    case FUEL_TYPES.ELECTRIC:
      decoration = 'bulb';
      selectedPreference = selectedMeterReadPreferences.electric;
      break;
    case FUEL_TYPES.GAS:
      decoration = 'gas';
      selectedPreference = selectedMeterReadPreferences.gas;
      break;
    default:
      decoration = 'dual-fuel';
      selectedPreference = selectedMeterReadPreferences.electric;
  }

  const displayedFuelName =
    fuel === 'dual' ? 'gas and electricity' : FUEL_NAMES[fuel];

  const handleChange = (event) => {
    const { value } = event.target;
    smartMeterByFuel.forEach((fuel) => {
      dispatch(
        setMeterReadPreferences({
          fuel,
          value: value
        })
      );
      dispatch(
        pushEvent({
          category: 'portal_smartmeter_frequency_final',
          action: fuel,
          label: value
        })
      );
    });
  };

  if (shouldNotRender) {
    return null;
  }

  return (
    <div data-testid={testId}>
      <NsContent>
        <NsFormGroup
          legend={`How often would you like your smart ${displayedFuelName} meter to send us readings?`}
          id="meterReadPreferences"
        >
          <NsList className="ul-bullet">
            {[
              `The more often your smart meters send us readings, the easier it is for you to see how much energy you're using.`,
              `This can help manage your energy usage, potentially cutting costs.`,
              `We may use half-hourly data to offer you tailored energy products and services.`,
              `Remember, it's your data and you're always in control and your settings can be changed at any time in your account.`
            ]}
          </NsList>
          <NsSelector
            name={fuel}
            decoration={decoration}
            onChange={handleChange}
            selectedValue={selectedPreference}
            columns={{
              basketballcourt: '1',
              hockeypitch: '1',
              footballpitch: '1',
              rugbypitch: '1',
              polofield: '1'
            }}
            options={[
              {
                value: METER_READ_TYPES.HALF_HOURLY,
                title: 'Half-hourly (recommended)'
              },
              {
                value: METER_READ_TYPES.MONTHLY,
                title: 'Monthly'
              }
            ]}
          />
          <p className="p-caption">
            Find out more about how we use your data in our{' '}
            <NsAnchor href={actionUrl.privacy}>
              Smart Meter Privacy Notice
            </NsAnchor>
            .
          </p>
        </NsFormGroup>
      </NsContent>
    </div>
  );
};
